import { CoreError, CoreErrorCause } from '@domain/models/core-error'

export const UserErrorCause = {
  UserNotFound: 'user_not_found',
  InvalidUserData: 'invalid_user_data',
} as const

export type UserErrorCause =
  (typeof UserErrorCause)[keyof typeof UserErrorCause]

function getErrorCode(error: Error): string | null {
  if ('code' in error && typeof error.code === 'string') {
    return error.code
  }
  return null
}

export class UserErrorConverter {
  public static domainModel(fromExternalModel: unknown): CoreError {
    if (fromExternalModel instanceof CoreError) {
      return fromExternalModel
    }

    if (!(fromExternalModel instanceof Error)) {
      return new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `An unknown user service error happened: ${String(fromExternalModel)}`,
        underlyingException: fromExternalModel,
      })
    }

    const code = getErrorCode(fromExternalModel)
    switch (code) {
      case 'permission-denied':
        return new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: 'Permission denied while accessing user data.',
          underlyingException: fromExternalModel,
        })
      case 'not-found':
        return new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: 'User data was not found.',
          underlyingException: fromExternalModel,
        })
      case 'unavailable':
      case 'network-request-failed':
        return new CoreError({
          cause: CoreErrorCause.NetworkConnection,
          context: 'User data is currently unavailable due to a network issue.',
          underlyingException: fromExternalModel,
        })
      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: fromExternalModel.message || 'Failed to process user data.',
          underlyingException: fromExternalModel,
        })
    }
  }

  public static createUserError(cause: UserErrorCause, message: string): CoreError {
    switch (cause) {
      case UserErrorCause.UserNotFound:
        return new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: message,
        })
      case UserErrorCause.InvalidUserData:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: message,
        })
      default:
        return new CoreError({
          cause: CoreErrorCause.Unknown,
          context: message,
        })
    }
  }
}
